import { useEffect, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

/**
 * Custom EntityPicker for Digital Hub components.
 *
 * Displays the original metadata.name (preserving underscores and casing)
 * instead of the normalized entity ref that the standard EntityPicker shows.
 *
 * ui:options:
 *   deployedEnv?: 'pre' | 'pro'   — filter by last deployed environment
 *   label?: string                 — custom field label
 */
export const DigitalHubEntityPickerField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  const deployedEnv = uiSchema['ui:options']?.deployedEnv as string | undefined;
  const label = (uiSchema['ui:options']?.label as unknown as string) || 'Componente Digital Hub';

  const [entities, setEntities] = useState<Entity[]>([]);

  useEffect(() => {
    const filter: Record<string, string> = {
      kind: 'Component',
      'metadata.annotations.example.com/provisioning-profile': 'digitalHub',
    };
    if (deployedEnv) {
      filter['metadata.annotations.example.com/digital-hub-last-deployed-env'] =
        deployedEnv;
    }

    catalogApi
      .getEntities({ filter, fields: ['kind', 'metadata.name', 'metadata.namespace'] })
      .then(resp => setEntities(resp.items))
      .catch(() => setEntities([]));
  }, [catalogApi, deployedEnv]);

  const selectedEntity =
    entities.find(e => stringifyEntityRef(e) === formData) ?? null;

  return (
    <FormControl margin="normal" required={required} fullWidth>
      <Autocomplete
        options={entities}
        getOptionLabel={opt => opt.metadata.name}
        value={selectedEntity}
        onChange={(_, newValue) => {
          onChange(newValue ? stringifyEntityRef(newValue) : (undefined as any));
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            helperText={rawErrors?.[0] || 'Selecciona el componente a desplegar'}
            variant="outlined"
            required={required}
            error={Boolean(rawErrors?.length)}
          />
        )}
      />
    </FormControl>
  );
};

export const DigitalHubEntityPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'DigitalHubEntityPicker',
    component: DigitalHubEntityPickerField,
  }),
);
